import { type AxiosPromise } from "axios";
import { FileDropzone } from "components/FileUpload/FileDropzone";
import React from "react";
import { Message, Progress } from "semantic-ui-react";
import { type UploadFilesConfig, type uploadFilesToCoreApi } from "utilities/fileUploadUtils";
import { DeleteDialog } from "../../../components";
import { NoDataAvailableDiv } from "../../../components/NoDataAvailableDiv";
import EditUploadedFile from "./edituploadedfile";
import UploadedFile from "./uploadedfile";

interface Props {
  files: server.dto.File[]
  upload: (files: File[], uploadConfig: UploadFilesConfig) => ReturnType<typeof uploadFilesToCoreApi>
  deleteEndpoint: (id: number) => AxiosPromise<void>
  renameEndpoint: (file: server.dto.File) => AxiosPromise<server.dto.File>
  onFilesChanged: () => void
  canEdit: boolean
}

interface State {
  editingFileId?: number
  deletingFileId?: number
  isUploading: boolean
  uploadProgress: number
  uploadError: string
}

export default class FileUploadModal extends React.Component<Props, State> {
  state: State = {
    editingFileId: null,
    isUploading: false,
    uploadProgress: 0,
    uploadError: undefined
  };

  onFileNameChanged = async (newFile: server.dto.File) => {
    await this.props.renameEndpoint(newFile);
    this.setState({ editingFileId: null });

    this.props.onFilesChanged();
  };

  onEditClicked = (file: server.dto.File) => {
    this.setState({ editingFileId: file.id });
  };

  onDeleteClicked = (file: server.dto.File) => {
    this.setState({ deletingFileId: file.id });
  };

  onDelete = async () => {
    await this.props.deleteEndpoint(this.state.deletingFileId);
    this.setState({ deletingFileId: null });
    this.props.onFilesChanged();
  };

  onDrop = async (files: File[]) => {
    this.setState({ isUploading: true, uploadProgress: 10, uploadError: "" });

    try {
      const uploadFilesRes = this.props.upload(files, {
        onUploadProgress: (e: ProgressEvent) => {
          const percentCompleted = Math.round((e.loaded * 100) / e.total);
          this.setState({ uploadProgress: percentCompleted });
        },
        fileType: "any"
      });

      if (typeof uploadFilesRes === "string") {
        this.setState({
          isUploading: false,
          uploadProgress: 0,
          uploadError: uploadFilesRes
        });
      } else {
        await uploadFilesRes;
        this.setState({ isUploading: false, uploadProgress: 0 });
        this.props.onFilesChanged();
      }
    } catch {
      this.setState({ isUploading: false, uploadProgress: 0 });
    }
  };

  public render(): JSX.Element {
    return <>
      <div className="grow1 scrollbox-auto margin-bottom-large">
        {this.props.files.length === 0 && <NoDataAvailableDiv>{RESX.File.NoFilesUploaded}</NoDataAvailableDiv>}

        {this.props.files.length > 0 && <div className="list-with-gaps line-layout">
          <div className="list-with-gaps-body">
            {this.props.files.map(file => this.state.editingFileId === file.id
              ? <EditUploadedFile key={file.id} onFileNameChanged={this.onFileNameChanged} file={file} />
              : <UploadedFile key={file.id} file={file} onEditClicked={this.onEditClicked} onDeleteClicked={this.onDeleteClicked} canEdit={this.props.canEdit} />)}
          </div>
        </div>}
      </div>

      {this.state.isUploading && <div className="white-text">
        <Progress
          percent={this.state.uploadProgress}
          color="green"
          progress={true}
          active={true}>{RESX.File.UploadingFiles}</Progress>
      </div>}
      {!this.state.isUploading && <>
        {this.state.uploadError && <Message color="red" className="margin-bottom">
          {this.state.uploadError}
        </Message>}
        <div>
          <FileDropzone
            onDrop={this.onDrop}
            onDropReject={(fileRejections) => {
              this.setState({
                isUploading: false,
                uploadProgress: 0,
                uploadError: fileRejections[0].errors[0].message
              });
            }}
            maxSizeBytes={serverReference.constants.file.maximumTravelFileContentLength}
            iconType="file"
          />
        </div>
      </>}
      <DeleteDialog
        isOpen={!!this.state.deletingFileId}
        message={RESX.GeneralWarnings.AreYouSure}
        handleClose={() => { this.setState({ deletingFileId: null }); }}
        onDelete={this.onDelete}
      />
    </>;
  }
}
